import { useEffect, useMemo, useState } from 'react';
import { Braces, CalendarClock, ChevronDown, Loader2, Sparkles, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import {
  DEFAULT_PERSONALIZATION_CONFIG,
  parseScheduledEmailPersonalization,
} from '../../lib/personalization';
import PersonalizationPreviewPanel from './PersonalizationPreviewPanel';
import SettingsFieldHelpPopover from '../Common/SettingsFieldHelpPopover';
import type { ScheduledEmailRow, ScheduledEmailStatus } from './types';

interface ScheduleEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscriberId: string;
  subscriberEmail?: string | null;
  /**
   * When supplied, the modal switches to edit mode and pre-fills from this
   * row. When null/undefined, the modal is in create mode.
   */
  existing?: ScheduledEmailRow | null;
  onSuccess?: () => void;
}

const DEFAULT_SUBJECT = 'Hello World';
const DEFAULT_BODY_HTML = '<p>Hello world!</p>';

const STATUS_OPTIONS: ScheduledEmailStatus[] = [
  'pending',
  'sending',
  'sent',
  'failed',
  'canceled',
];

const STATUS_LABELS: Record<ScheduledEmailStatus, string> = {
  pending: 'Pending',
  sending: 'Sending',
  sent: 'Sent',
  failed: 'Failed',
  canceled: 'Canceled',
};

function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInputValue(s: string): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export default function ScheduleEmailModal({
  isOpen,
  onClose,
  subscriberId,
  subscriberEmail,
  existing,
  onSuccess,
}: ScheduleEmailModalProps) {
  const isEdit = !!existing;

  const [name, setName] = useState('');
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [bodyHtml, setBodyHtml] = useState(DEFAULT_BODY_HTML);
  const [scheduledAtLocal, setScheduledAtLocal] = useState('');
  const [status, setStatus] = useState<ScheduledEmailStatus>('pending');
  const [aiPersonalizationEnabled, setAiPersonalizationEnabled] = useState(false);
  const [aiMinScore, setAiMinScore] = useState(DEFAULT_PERSONALIZATION_CONFIG.min_score);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [jsonDebugOpen, setJsonDebugOpen] = useState(false);
  const [aiPreviewOpen, setAiPreviewOpen] = useState(false);

  const existingJsonString = useMemo(() => {
    if (!existing) return '';
    try {
      return JSON.stringify(existing, null, 2);
    } catch {
      return '{}';
    }
  }, [existing]);

  const defaultScheduledAt = useMemo(() => {
    const d = new Date();
    // Default: send in 5 minutes so the next cron tick can pick it up.
    d.setMinutes(d.getMinutes() + 5);
    d.setSeconds(0, 0);
    return toLocalInputValue(d);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setJsonDebugOpen(false);
      setAiPreviewOpen(false);
      return;
    }
    if (existing) {
      setName(existing.name ?? '');
      setSubject(existing.subject ?? DEFAULT_SUBJECT);
      setBodyHtml(existing.body_html ?? DEFAULT_BODY_HTML);
      const when = existing.scheduled_at ? new Date(existing.scheduled_at) : null;
      setScheduledAtLocal(when && !Number.isNaN(when.getTime()) ? toLocalInputValue(when) : defaultScheduledAt);
      setStatus(existing.status);
      setAiPersonalizationEnabled(!!parseScheduledEmailPersonalization(existing.personalization)?.enabled);
    } else {
      setName('');
      setSubject(DEFAULT_SUBJECT);
      setBodyHtml(DEFAULT_BODY_HTML);
      setScheduledAtLocal(defaultScheduledAt);
      setStatus('pending');
      setAiPersonalizationEnabled(false);
      setAiMinScore(DEFAULT_PERSONALIZATION_CONFIG.min_score);
    }
  }, [isOpen, existing, defaultScheduledAt]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving && !deleting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, saving, deleting]);

  const handleSave = async () => {
    if (!subscriberId) {
      toast.error('Missing subscriber id');
      return;
    }
    const subjectTrim = subject.trim();
    if (!subjectTrim) {
      toast.error('Subject is required');
      return;
    }
    const bodyTrim = bodyHtml.trim();
    if (!bodyTrim) {
      toast.error('Body is required');
      return;
    }
    const when = fromLocalInputValue(scheduledAtLocal);
    if (!when) {
      toast.error('Scheduled date/time is required');
      return;
    }

    setSaving(true);
    try {
      if (existing) {
        // Edit mode: write the form fields. When the user moves the row back
        // to `pending` from a terminal status, clear the failure metadata so
        // the next cron run treats it like a fresh attempt.
        const patch: Record<string, unknown> = { status };
        if (status !== 'sent') {
          patch.name = name.trim() || null;
          patch.subject = subjectTrim;
          patch.body_html = bodyTrim;
          patch.scheduled_at = when.toISOString();
        }
        const wasTerminal = existing.status !== 'pending';
        if (status === 'pending' && wasTerminal) {
          patch.sent_at = null;
          patch.error = null;
          patch.provider = null;
          patch.attempts = 0;
        }

        const { error } = await supabase
          .from('scheduled_emails')
          .update(patch)
          .eq('id', existing.id);
        if (error) throw error;
        toast.success('Scheduled email updated');
      } else {
        const personalization = aiPersonalizationEnabled
          ? {
              enabled: true,
              min_score: aiMinScore,
              status: 'pending' as const,
              base: { subject: subjectTrim, body_html: bodyTrim },
            }
          : null;
        const { error } = await supabase.from('scheduled_emails').insert({
          subscriber_id: subscriberId,
          name: name.trim() || null,
          subject: subjectTrim,
          body_html: bodyTrim,
          scheduled_at: when.toISOString(),
          status: 'pending',
          personalization,
        });
        if (error) throw error;
        toast.success('Email scheduled');
      }
      onSuccess?.();
      onClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existing) return;
    const label = existing.name || existing.subject || existing.id;
    if (!window.confirm(`Delete scheduled email: ${label}?`)) return;

    setDeleting(true);
    try {
      const { error } = await supabase.from('scheduled_emails').delete().eq('id', existing.id);
      if (error) throw error;
      toast.success('Scheduled email deleted');
      onSuccess?.();
      onClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen) return null;
  const busy = saving || deleting;
  const sendContentLocked = isEdit && status === 'sent';

  const personalizationMinScore =
    parseScheduledEmailPersonalization(existing?.personalization)?.min_score ??
    aiMinScore;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div
        className={`bg-white dark:bg-gray-800 shadow-xl w-full flex flex-row min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl ${
          aiPreviewOpen ? 'max-w-7xl' : 'max-w-3xl'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-email-modal-title"
      >
        <div className="flex flex-col flex-1 min-w-0 min-h-0">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2
            id="schedule-email-modal-title"
            className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"
          >
            <CalendarClock className="w-5 h-5 text-emerald-500" />
            {isEdit ? 'Edit scheduled email' : 'Schedule email'}
          </h2>
          <div className="flex items-center gap-1">
            <button
              onClick={onClose}
              disabled={busy}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          {subscriberEmail ? (
            <div className="flex items-center justify-between gap-4 text-xs text-gray-500 dark:text-gray-400">
              <div className="min-w-0">
                {isEdit ? 'Linked to ' : 'Sending to '}
                <span className="font-medium text-gray-700 dark:text-gray-200">{subscriberEmail}</span>
              </div>
              {name.trim() ? (
                <span
                  className="font-medium text-gray-700 dark:text-gray-200 text-right truncate max-w-[50%] shrink-0"
                  title={name.trim()}
                >
                  {name.trim()}
                </span>
              ) : null}
            </div>
          ) : null}

          <div>
            <label
              htmlFor="schedule-email-subject"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Subject
            </label>
            <input
              id="schedule-email-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              disabled={sendContentLocked}
              title={sendContentLocked ? 'Cannot edit subject after send' : undefined}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-900/50"
              autoFocus={!sendContentLocked}
            />
          </div>

          <div
            className={`grid grid-cols-1 gap-4 items-end ${
              isEdit ? 'sm:grid-cols-3' : 'sm:grid-cols-[minmax(0,1fr)_auto]'
            }`}
          >
            <div>
              <div className="flex items-center gap-1 mb-1">
                <label
                  htmlFor="schedule-email-when"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Scheduled for
                </label>
                <SettingsFieldHelpPopover
                  panelTitle="Scheduled for"
                  ariaLabel="Scheduled for help"
                  intro="Picked up by the cron at the next 5-minute tick after this time."
                />
              </div>
              <input
                id="schedule-email-when"
                type="datetime-local"
                value={scheduledAtLocal}
                onChange={(e) => setScheduledAtLocal(e.target.value)}
                required
                disabled={sendContentLocked}
                title={sendContentLocked ? 'Cannot edit scheduled time after send' : undefined}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-900/50"
              />
            </div>

            {isEdit ? (
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <label
                    htmlFor="schedule-email-status"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Status
                  </label>
                  <SettingsFieldHelpPopover
                    panelTitle="Status"
                    ariaLabel="Status help"
                    intro={
                      status === 'pending' && existing && existing.status !== 'pending'
                        ? 'Will clear sent / error / attempts so the cron retries from scratch.'
                        : 'Set to canceled to stop a pending send.'
                    }
                  />
                </div>
                <div className="relative">
                  <select
                    id="schedule-email-status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as ScheduledEmailStatus)}
                    className="w-full appearance-none pl-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABELS[s]}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500"
                    aria-hidden
                  />
                </div>
              </div>
            ) : null}

            <div className="min-w-0">
              <div className="flex items-center gap-1 mb-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Personalize</span>
                <SettingsFieldHelpPopover
                  panelTitle="Personalize"
                  ariaLabel="Personalize help"
                  align="right"
                  intro={
                    isEdit
                      ? 'Enable on create only; edit personalization via scheduled row metadata.'
                      : 'Runs before send when subscriber profile score is high enough.'
                  }
                />
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <div className="h-[42px] flex items-center">
                  <label
                    className={`relative inline-flex items-center shrink-0 ${
                      isEdit ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                    }`}
                    title={
                      isEdit
                        ? 'Personalization is set at create time only.'
                        : aiPersonalizationEnabled
                          ? 'Personalize per subscriber when profile score meets minimum.'
                          : 'Send the base message to everyone.'
                    }
                  >
                    <input
                      type="checkbox"
                      checked={aiPersonalizationEnabled}
                      onChange={(e) => setAiPersonalizationEnabled(e.target.checked)}
                      disabled={isEdit}
                      className="sr-only peer"
                    />
                    <div className="w-10 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer-checked:bg-emerald-500 peer-disabled:opacity-50 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-4" />
                  </label>
                </div>
                {aiPersonalizationEnabled && !isEdit ? (
                  <div>
                    <label
                      htmlFor="schedule-email-min-score"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Min score
                    </label>
                    <input
                      id="schedule-email-min-score"
                      type="number"
                      min={0}
                      max={100}
                      value={aiMinScore}
                      onChange={(e) =>
                        setAiMinScore(Math.max(0, Math.min(100, Number(e.target.value) || 0)))
                      }
                      className="w-20 px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div>
            <label
              htmlFor="schedule-email-body"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Body (Markdown or HTML)
            </label>
            <textarea
              id="schedule-email-body"
              value={bodyHtml}
              onChange={(e) => setBodyHtml(e.target.value)}
              required
              rows={8}
              disabled={sendContentLocked}
              title={sendContentLocked ? 'Cannot edit body after send' : undefined}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-900/50"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Markdown is converted to HTML at send time. Use {'{{first_name}}'} for merge fields.
              Blank subject or body falls back to a &ldquo;hello world&rdquo; default.
            </p>
          </div>

          {isEdit && existing ? (
            (() => {
              const p = parseScheduledEmailPersonalization(existing.personalization);
              if (!p) return null;
              return (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-xs space-y-2">
                  <div className="font-medium text-gray-700 dark:text-gray-200">
                    AI personalization ({p.status})
                  </div>
                  {p.base.subject || p.base.body_html ? (
                    <details className="text-gray-600 dark:text-gray-300">
                      <summary className="cursor-pointer">View base vs send copy</summary>
                      <div className="mt-2 space-y-1 font-mono break-all">
                        <div>Base subject: {p.base.subject ?? '—'}</div>
                        {p.result?.subject ? (
                          <div>Sent subject: {p.result.subject}</div>
                        ) : null}
                      </div>
                    </details>
                  ) : null}
                </div>
              );
            })()
          ) : null}

          {isEdit && existing ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-gray-500 dark:text-gray-400">
              <div className="break-words">
                <span className="font-medium text-gray-600 dark:text-gray-300">Inbox preview:</span>{' '}
                {existing.subtitle?.trim() || '—'}
              </div>
              <div className="break-words font-mono">
                <span className="font-medium text-gray-600 dark:text-gray-300 font-sans">
                  Postmark alias:
                </span>{' '}
                {existing.postmark_template_alias?.trim() || '—'}
              </div>
              <div>
                <span className="font-medium text-gray-600 dark:text-gray-300">Content mode:</span>{' '}
                {existing.content_mode?.trim() || '—'}
              </div>
              <div>
                <span className="font-medium text-gray-600 dark:text-gray-300">Sent at:</span>{' '}
                {existing.sent_at ? new Date(existing.sent_at).toLocaleString() : '—'}
              </div>
              <div>
                <span className="font-medium text-gray-600 dark:text-gray-300">Attempts:</span>{' '}
                {existing.attempts}
              </div>
              <div className="break-words">
                <span className="font-medium text-gray-600 dark:text-gray-300">Provider:</span>{' '}
                {existing.provider || '—'}
              </div>
              {existing.error ? (
                <div className="sm:col-span-2 break-words text-red-700 dark:text-red-300">
                  <span className="font-medium">Last error:</span> {existing.error}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-2">
            {subscriberId ? (
              <button
                type="button"
                onClick={() => setAiPreviewOpen((v) => !v)}
                disabled={busy}
                title="This opens up an AI Preview tool to see how AI customizations would impact the message."
                aria-label="AI Preview — see how AI customizations would impact the message"
                className={`inline-flex items-center justify-center p-2 rounded-lg border transition-colors disabled:opacity-50 ${
                  aiPreviewOpen
                    ? 'border-violet-400 bg-violet-50 text-violet-800 dark:bg-violet-900/30 dark:text-violet-200 dark:border-violet-600'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <Sparkles className="w-4 h-4" />
              </button>
            ) : null}
            {isEdit && existing ? (
              <>
                <button
                  type="button"
                  onClick={() => setJsonDebugOpen(true)}
                  disabled={busy}
                  title="View scheduled email JSON"
                  aria-label="View scheduled email JSON"
                  className="inline-flex items-center justify-center p-2 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50 rounded-lg transition-colors"
                >
                  <Braces className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => void handleDelete()}
                  disabled={busy}
                  title="Delete scheduled email"
                  aria-label="Delete scheduled email"
                  className="inline-flex items-center justify-center p-2 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50 rounded-lg transition-colors"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              </>
            ) : null}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={busy}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => void handleSave()}
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 rounded-lg transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />}
              {saving ? 'Saving…' : isEdit ? 'Save' : 'Schedule email'}
            </button>
          </div>
        </div>
        </div>

        {aiPreviewOpen && subscriberId ? (
          <PersonalizationPreviewPanel
            onClose={() => setAiPreviewOpen(false)}
            subscriberId={subscriberId}
            subscriberEmail={subscriberEmail}
            subject={subject}
            bodyHtml={bodyHtml}
            minScore={personalizationMinScore}
            existing={existing}
          />
        ) : null}
      </div>

      {jsonDebugOpen && existing ? (
        <div
          className="fixed inset-0 z-[60] flex items-stretch sm:items-center justify-center p-0 sm:p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="schedule-email-json-debug-title"
          onClick={() => setJsonDebugOpen(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 shadow-xl max-w-2xl w-full flex flex-col border border-gray-200 dark:border-gray-700 min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <h3
                id="schedule-email-json-debug-title"
                className="text-sm font-semibold text-gray-900 dark:text-white truncate pr-2"
              >
                Scheduled email JSON (debug)
              </h3>
              <button
                type="button"
                onClick={() => setJsonDebugOpen(false)}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0"
                aria-label="Close debug panel"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <pre className="flex-1 overflow-auto p-4 text-xs font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-all">
              {existingJsonString}
            </pre>
          </div>
        </div>
      ) : null}
    </div>
  );
}
